import React, { useState } from "react";
import { Users } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import type { WorkspaceItem, WorkspaceDetail, MemberRow } from "../../types";
import { getErrorMessage } from "../../types";

export interface AddWorkspaceMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  workspace: WorkspaceItem;
  detailedWorkspace: WorkspaceDetail | null;
  members: MemberRow[];
  onSuccess: () => Promise<void>;
}

export const AddWorkspaceMemberModal: React.FC<AddWorkspaceMemberModalProps> = ({
  isOpen,
  onClose,
  slug,
  workspace,
  detailedWorkspace,
  members,
  onSuccess,
}) => {
  const [selectedUserIdToAdd, setSelectedUserIdToAdd] = useState<number | "">("");
  const [addWsMemberRole, setAddWsMemberRole] = useState("member");
  const [addingWsMember, setAddingWsMember] = useState(false);
  const [addWsMemberError, setAddWsMemberError] = useState<string | null>(null);

  const resetForm = () => {
    setSelectedUserIdToAdd("");
    setAddWsMemberRole("member");
    setAddWsMemberError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !workspace || !selectedUserIdToAdd) return;
    try {
      setAddingWsMember(true);
      setAddWsMemberError(null);
      const { data, error } = await api.addWorkspaceMemberApiV1B2bSlugWorkspacesWsSlugMembersPost({
        path: { slug, ws_slug: workspace.slug },
        body: {
          user_id: Number(selectedUserIdToAdd),
          role: addWsMemberRole,
        },
      });
      if (data) {
        handleClose();
        await onSuccess();
      } else {
        setAddWsMemberError(getErrorMessage(error, "Failed to add member to workspace"));
      }
    } catch (err) {
      setAddWsMemberError("Network error adding member");
    } finally {
      setAddingWsMember(false);
    }
  };

  const isCorrectWorkspace = detailedWorkspace && detailedWorkspace.slug === workspace.slug;
  const existingUserIds = new Set(
    isCorrectWorkspace ? (detailedWorkspace.members || []).map((m) => m.user_id) : []
  );
  const eligibleMembers = members.filter((m) => !existingUserIds.has(m.user_id));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add Member to ${workspace.name}`}
      icon={<Users className="w-5 h-5 text-indigo-600" />}
      maxWidth="max-w-md"
    >
      {addWsMemberError && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold">
          {addWsMemberError}
        </div>
      )}

      {!isCorrectWorkspace ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-slate-500 font-medium">Loading workspace members...</p>
        </div>
      ) : eligibleMembers.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-xs text-slate-600 mb-4">
            All active members in this organization are already enrolled in {workspace.name}.
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl font-semibold text-xs"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-slate-700 mb-1.5">
              Select Organization Member *
            </label>
            <select
              required
              value={selectedUserIdToAdd}
              onChange={(e) => setSelectedUserIdToAdd(e.target.value ? Number(e.target.value) : "")}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
            >
              <option value="">-- Choose a member --</option>
              {eligibleMembers.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name ? `${m.name} (${m.email})` : m.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1.5">Workspace Role</label>
            <select
              value={addWsMemberRole}
              onChange={(e) => setAddWsMemberRole(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
            >
              <option value="member">Member</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addingWsMember || !selectedUserIdToAdd}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50"
            >
              {addingWsMember ? "Adding..." : "Add to Workspace"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};
